import React from 'react';

import useSearchStore from '../../_Global/SearchStore';
import {renderHTML} from '../../_Global/searchReactHelpers';

interface FacetAccordionConfiguration {
    facetTitle: string;
    facetKey: string;
    facetParamName: string;
    facetType: FacetType;
    showCount: boolean;
    isActive: boolean;
    onToggle(string): void;
}

function FacetAccordion({facetTitle, facetKey, facetParamName, facetType, showCount, isActive, onToggle}: FacetAccordionConfiguration) {
    const searchFacetsJson = useSearchStore((state) => state.getSearchFacetDataByKey(facetKey));
    const selectedFacetOptions = useSearchStore((state) => state.getSearchFacetSelectionsByKey(facetParamName));
    const setSearchFacetSelectionsByKey = useSearchStore((state) => state.setSearchFacetSelectionsByKey);
    const setQueueScrollToForm = useSearchStore((state) => state.setQueueScrollToForm);

    /**
     * Respond to clicks on the accordion button
     */
    const onAccordionClick = (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();

        onToggle(isActive ? '' : facetTitle);
    };

    /**
     * Respond to changes to the facet checkbox option inputs.
     * Checkbox facets allow for multiple selections.
     */
    const toggleCheckboxFacetSelection = (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        const userHasChecked = evt.currentTarget.classList.contains('button--active');
        const checkboxValue = evt.currentTarget.getAttribute('name');
        const currentSelectedOptions = [...selectedFacetOptions];

        if (checkboxValue) {
            if (!userHasChecked) {
                // Remove this to the list of selected facets
                currentSelectedOptions.push(checkboxValue);
            } else {
                // Add this from the list of selected facets
                const valueIndex = currentSelectedOptions.indexOf(checkboxValue);
                currentSelectedOptions.splice(valueIndex, 1);
            }
        }

        // Scroll to the top of the form
        setQueueScrollToForm(true);

        // Update the global facets state
        setSearchFacetSelectionsByKey(facetParamName, currentSelectedOptions);
    };

    /**
     * Respond to changes to the facet radio option inputs.
     * Radio facets allow for a single selection.
     */
    const setRadioFacetSelection = (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        const userHasChecked = evt.currentTarget.classList.contains('button--active');
        const checkboxValue = evt.currentTarget.getAttribute('name');
        let currentSelectedOptions = [...selectedFacetOptions];

        if (checkboxValue) {
            if (!userHasChecked) {
                // Replace all the selected options with this new one
                currentSelectedOptions = [checkboxValue];
            }
        }

        // Scroll to the top of the form
        setQueueScrollToForm(true);

        // Update the global facets state
        setSearchFacetSelectionsByKey(facetParamName, currentSelectedOptions);
    };

    function generateFacetOptions(facetsJson: SearchFacetValue[], type: FacetType) {
        if (type === 'checkbox') {
            return facetsJson.map((option) => {
                let isChecked = false;

                if (selectedFacetOptions.indexOf(option.value) !== -1) {
                    isChecked = true;
                }

                return (
                    <button
                        className={`button button--filter ${isChecked ? 'button--active' : ''}`}
                        key={option.label}
                        onClick={toggleCheckboxFacetSelection}
                        id={crypto.randomUUID()}
                        name={option.value}
                    >
                        <svg className="button__icon">
                            <use xlinkHref="#tick"></use>
                        </svg>
                        {renderHTML('span', 'button__label body-size-sm', `${option.label} ${showCount ? <span>({option.count})</span> : ''}`)}
                    </button>
                );
            });
        }

        if (type === 'radio') {
            return facetsJson.map((option) => {
                let isChecked = false;

                if (selectedFacetOptions.indexOf(option.value) !== -1) {
                    isChecked = true;
                }

                return (
                    <button
                        className={`button button--filter ${isChecked ? 'button--active' : ''}`}
                        key={option.label}
                        onClick={setRadioFacetSelection}
                        id={crypto.randomUUID()}
                        name={option.value}
                    >
                        <svg className="button__icon">
                            <use xlinkHref="#tick"></use>
                        </svg>
                        <span className="button__label body-size-sm">
                            {option.label} {showCount ? <span>({option.count})</span> : null}
                        </span>
                    </button>
                );
            });
        }
    }

    return (
        <>
            <div className="search-filter-accordion__controls">
                <button
                    id={`filter-accordion-${facetTitle.replace(/\s/, '-')}`}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`filter-${facetTitle.replace(/\s/, '-')}-panel`}
                    className={`button button--tab body-size-xl ${isActive ? 'button--active' : ''}`}
                    aria-expanded={isActive}
                    onClick={onAccordionClick}
                    disabled={searchFacetsJson.length === 0}
                >
                    <div className={`icon__expand-container ${isActive && 'icon__expand--active'}`}>
                        <svg className="icon__expand-horizontal">
                            <use xlinkHref="#minus"></use>
                        </svg>
                        <svg className="icon__expand-vertical">
                            <use xlinkHref="#minus"></use>
                        </svg>
                    </div>
                    <span className="button__label">
                        {facetTitle}
                        {selectedFacetOptions.length !== 0 ? ` (${selectedFacetOptions.length})` : null}
                    </span>
                </button>
            </div>

            <div
                id={`filter-${facetTitle.replace(/\s/, '-')}-panel`}
                className={`search-filter-tabs__tab-content ${isActive ? 'tab-content--active-accordion' : ''}`}
                role="tabpanel"
                aria-labelledby={`${`filter-tab-${facetTitle.replace(/\s/, '-')}`} ${`filter-accordion-${facetTitle.replace(/\s/, '-')}`}`}
            >
                <div className="search-filter-tabs__tab-options-wrapper">
                    <div className="search-filter-tabs__tab-options">
                        {generateFacetOptions(searchFacetsJson, facetType)}
                        {/* {searchFacetsJson.length === 0 ? <p className="">There are no {facetTitle} options available for the current search term or filters.</p> : null} */}
                    </div>
                </div>
            </div>
        </>
    );
}

export default React.memo(FacetAccordion);
